import { apiClient } from "../config/axios";
import type { Memory, MemoryStatus, UpdateMemoryDto, ImportResult, PaginatedResponse } from "./memory.service";

export interface AdminUpsertMemoryDto {
    question: string;
    answer: string;
    category?: string;
    // No clientId = global entry
}

export interface AdminListParams {
    status?: MemoryStatus;
    clientId?: string; // "global" for global-only, or a uuid
    page?: number;
    limit?: number;
}

export interface AdminSearchDto {
    query: string;
    status?: MemoryStatus;
    clientId?: string;
}

export interface BulkStatusResult {
    updated: number;
    notFound: string[];
}

export const adminMemoryService = {
    async list(params?: AdminListParams): Promise<PaginatedResponse<Memory>> {
        const query: Record<string, string> = {};
        if (params?.status) query.status = params.status;
        if (params?.clientId) query.clientId = params.clientId;
        query.page = String(params?.page ?? 1);
        query.limit = String(params?.limit ?? 20);
        const res = await apiClient.get<PaginatedResponse<Memory>>("/v1/admin/memory", { params: query });
        return res.data;
    },

    async getById(id: string): Promise<Memory> {
        const res = await apiClient.get<Memory>(`/v1/admin/memory/${id}`);
        return res.data;
    },

    async create(data: AdminUpsertMemoryDto): Promise<Memory> {
        const res = await apiClient.post<Memory>("/v1/admin/memory/upsert", data);
        return res.data;
    },

    async update(id: string, data: UpdateMemoryDto): Promise<Memory> {
        const res = await apiClient.patch<Memory>(`/v1/admin/memory/${id}`, data);
        return res.data;
    },

    async updateStatus(id: string, status: MemoryStatus): Promise<Memory> {
        const res = await apiClient.patch<Memory>(`/v1/admin/memory/${id}/status`, { status });
        return res.data;
    },

    async delete(id: string): Promise<void> {
        await apiClient.delete(`/v1/admin/memory/${id}`);
    },

    async bulkUpdateStatus(ids: string[], status: MemoryStatus): Promise<BulkStatusResult> {
        const res = await apiClient.patch<BulkStatusResult>("/v1/admin/memory/bulk/status", { ids, status });
        return res.data;
    },

    async bulkDelete(ids: string[]): Promise<{ deleted: number; notFound: string[] }> {
        const res = await apiClient.delete<{ deleted: number; notFound: string[] }>("/v1/admin/memory/bulk", {
            data: { ids },
        });
        return res.data;
    },

    async importExcel(file: File): Promise<ImportResult> {
        const formData = new FormData();
        formData.append("file", file);
        const res = await apiClient.post<ImportResult>("/v1/admin/memory/upsert/excel", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return res.data;
    },

    async importCSV(file: File): Promise<ImportResult> {
        const formData = new FormData();
        formData.append("file", file);
        const res = await apiClient.post<ImportResult>("/v1/admin/memory/upsert/csv", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return res.data;
    },

    async search(data: AdminSearchDto): Promise<Memory[]> {
        const res = await apiClient.post<Memory[]>("/v1/admin/memory/search", data);
        return res.data;
    },
};
